import { AxiosResponse } from 'axios';
import { DoneActorEvent } from 'xstate';
import {
  DECLARED_TYPES_BY_EXTENSION,
  DOCUMENTS_PICKER_EXTENSIONS,
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  MAX_FILE_NAME_LENGTH,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  STORABLE_EXTENSIONS,
  UNKNOWN_DECLARED_TYPES,
  toTypesLabel,
} from './constants';
import {
  INSURANCE_CERTIFICATE_UPLOAD,
  PARSE_EXTENSIONS_BY_WORKFLOW,
  canParseFile,
  getFileExtension,
  resolveParseWorkflow,
  type ParseWorkflowType,
} from './parseWorkflow';
import { AIParseJob, Context, DocumentWithAiJobs } from './types';

export { getFileExtension };

export const isAllowedExtension = (ext: string, allowed: readonly string[]): boolean =>
  allowed.includes(ext.toLowerCase());

/**
 * The extensions the Documents page uploads for a File Type and Category (MCV-1760, MCV-1802).
 * A document the vault parses (a CV, a packet, a malpractice insurance certificate) takes ONLY
 * the formats its parse workflow reads: nothing that cannot be parsed is filed as one. Every
 * other File Type takes every stored format. Before a File Type is chosen, anything some File
 * Type accepts.
 */
export const documentsUploadExtensions = (
  docType?: string,
  docCategory?: string
): readonly string[] => {
  if (!docType) return DOCUMENTS_PICKER_EXTENSIONS;
  const workflow = resolveParseWorkflow({ docType, docCategory: docCategory ?? '' });
  return workflow ? PARSE_EXTENSIONS_BY_WORKFLOW[workflow] : STORABLE_EXTENSIONS;
};

export interface UploadRejection {
  name: string;
  reason: 'size' | 'type' | 'mismatch';
  text: string;
  subText: string;
}

// A function, not a module-level table: constants.tsx and this module sit in an import cycle,
// so MAX_FILE_SIZE_MB is not yet initialised when this module first evaluates.
const rejectionReasonLabel = (reason: UploadRejection['reason']): string =>
  ({
    size: `over ${MAX_FILE_SIZE_MB}MB`,
    type: 'unsupported type',
    mismatch: 'type does not match its extension',
  })[reason];

/** "a.exe (unsupported type), b.pdf (over 20MB)": the rejected files and why, for a toast. */
export const listRejections = (rejected: UploadRejection[]): string =>
  rejected.map(({ name, reason }) => `${name} (${rejectionReasonLabel(reason)})`).join(', ');

export const getUnsupportedFileNames = (
  files: ArrayLike<File>,
  allowed: readonly string[]
): string[] =>
  Array.from(files)
    .filter((file) => !isAllowedExtension(getFileExtension(file.name), allowed))
    .map((file) => file.name);

export const unsupportedFileTypeSubText = (names: string[], allowed: readonly string[]): string =>
  `Supported file types: ${toTypesLabel(allowed)}. Unsupported: ${names.join(', ')}`;

/**
 * The rejection for a file an upload would refuse (too large, not an allowed type, or a
 * declared type that contradicts its extension), or null. Fast feedback only: the server
 * re-checks the extension, the declared type and the file's bytes.
 */
export const getUploadRejection = (
  file: Pick<File, 'name' | 'size'> & { type?: string },
  allowed: readonly string[]
): UploadRejection | null => {
  const { name } = file;
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      name,
      reason: 'size',
      text: 'Document is too large',
      subText: `Files must be ${MAX_FILE_SIZE_MB}MB or smaller. Please upload a smaller file.`,
    };
  }
  const ext = getFileExtension(name).toLowerCase();
  if (!isAllowedExtension(ext, allowed)) {
    return {
      name,
      reason: 'type',
      text: 'Unsupported file type',
      subText: unsupportedFileTypeSubText([name], allowed),
    };
  }
  const declared = (file.type ?? '').split(';')[0].trim().toLowerCase();
  const agreeing = DECLARED_TYPES_BY_EXTENSION[ext];
  if (declared && !UNKNOWN_DECLARED_TYPES.includes(declared) && !agreeing?.includes(declared)) {
    return {
      name,
      reason: 'mismatch',
      text: 'Unsupported file type',
      subText: `"${name}" does not look like a .${ext} file (${declared}). Rename it with the correct extension or choose another file.`,
    };
  }
  return null;
};

export const splitUploadable = (
  files: ArrayLike<File>,
  allowed: readonly string[]
): { accepted: File[]; rejected: UploadRejection[] } => {
  const accepted: File[] = [];
  const rejected: UploadRejection[] = [];
  Array.from(files).forEach((file) => {
    const rejection = getUploadRejection(file, allowed);
    if (rejection) rejected.push(rejection);
    else accepted.push(file);
  });
  return { accepted, rejected };
};

/** The alert popup for rejected files. It names the supported formats. */
export const getRejectionAlert = (
  rejected: UploadRejection[],
  allowed: readonly string[]
): { text: string; subText: string } => {
  if (rejected.length === 1) return { text: rejected[0].text, subText: rejected[0].subText };
  if (rejected.every(({ reason }) => reason === 'type')) {
    return {
      text: 'Unsupported file type',
      subText: unsupportedFileTypeSubText(
        rejected.map(({ name }) => name),
        allowed
      ),
    };
  }
  return {
    text: 'Some files cannot be uploaded',
    subText: `Not uploaded: ${listRejections(rejected)}. Supported file types: ${toTypesLabel(allowed)}.`,
  };
};

/** The inline message under the file box (MCV-1577), for the same rejections as the popup. */
export const getInlineRejectionText = (
  rejected: UploadRejection[],
  allowed: readonly string[]
): string => {
  if (rejected.length === 1) {
    const [only] = rejected;
    if (only.reason === 'type') {
      const ext = getFileExtension(only.name).toLowerCase();
      return ext
        ? `.${ext} not supported for this file type`
        : `"${only.name}" has no file extension. Supported file types: ${toTypesLabel(allowed)}.`;
    }
    if (only.reason === 'size') {
      return `${only.text}. Files must be ${MAX_FILE_SIZE_MB}MB or smaller.`;
    }
  }
  return getRejectionAlert(rejected, allowed).subText;
};

type UploadErrorKind = 'too-large' | 'unsupported' | 'failed';

const classifyUploadError = (error: unknown): { kind: UploadErrorKind; detail: unknown } => {
  const err = (error ?? {}) as {
    status?: number;
    message?: unknown;
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const status = err.response?.status ?? err.status;
  // Detect 413 / "too large" errors that slipped past the client-side size check
  // (e.g. proxy or intermediate gateway rejection).
  if (status === 413 || /too large|FILE_TOO_LARGE|exceeds/i.test(String(err.message ?? ''))) {
    return { kind: 'too-large', detail: undefined };
  }
  if (status === 415) return { kind: 'unsupported', detail: err.response?.data?.detail };
  return { kind: 'failed', detail: undefined };
};

/**
 * The alert for an upload the API (or the network) refused. The API answers 415 with a readable
 * `detail` naming what is wrong with the file (extension, declared type or contents). A
 * `combined` upload (several files merged into one packet) words the 415 as a combine failure.
 */
export const getUploadErrorAlert = (
  error: unknown,
  { combined = false }: { combined?: boolean } = {}
): { text: string; subText: string } => {
  const { kind, detail } = classifyUploadError(error);
  if (kind === 'too-large') {
    return {
      text: 'Document is too large',
      subText: `Files must be ${MAX_FILE_SIZE_MB}MB or smaller. Please upload a smaller file.`,
    };
  }
  if (kind === 'unsupported') {
    return {
      text: combined ? 'Files could not be combined' : 'Unsupported file type',
      subText:
        typeof detail === 'string' && detail.trim()
          ? detail.trim()
          : 'This file type or its contents are not supported.',
    };
  }
  return {
    text: 'Failed to upload document',
    subText: 'Something went wrong uploading this document. Please try again.',
  };
};

/** A few words on why an upload failed, for the "Not uploaded: ..." list of a batch summary. */
export const uploadFailureReason = (error: unknown): string =>
  ({
    'too-large': 'too large',
    unsupported: 'type or contents not supported',
    failed: 'upload failed',
  })[classifyUploadError(error).kind];

/**
 * The name cut to MAX_FILE_NAME_LENGTH characters with its extension kept (mcv.document_info
 * stores doc_name and uploaded_file_name as VARCHAR(64), so a longer name used to fail the
 * upload with a 500). Counts code points, as the database does. The API applies the same rule
 * (fit_file_name in routes/document/utils.py) as a backstop.
 */
export const trimFileName = (name: string, max: number = MAX_FILE_NAME_LENGTH): string => {
  const chars = Array.from(name);
  if (chars.length <= max) return name;
  const dot = name.lastIndexOf('.');
  const suffixLength = dot === -1 ? 0 : Array.from(name.slice(dot)).length;
  if (suffixLength === 0 || suffixLength >= max) return chars.slice(0, max).join('');
  return (
    chars
      .slice(0, max - suffixLength)
      .join('')
      .trimEnd() + name.slice(dot)
  );
};

// [MCV-1761] A second parse merges into the vault again, so the confirm names that risk.
export const REPARSE_CONFIRM_MESSAGE =
  'This document has already been parsed. Parsing it again may add duplicate entries to your vault. Continue?';

/** Whether a manual parse asks first: a document with a completed parse would parse again. */
export const needsReparseConfirm = (doc: Pick<DocumentWithAiJobs, 'aiJobs'> | undefined): boolean =>
  doc?.aiJobs?.some((job) => job.job_status === 'COMPLETED') === true;

const PARSE_WORKFLOW_NOUNS: Record<ParseWorkflowType, string> = {
  'cv-parse': 'CVs',
  'packet-parse': 'Packets',
  'insurance-parse': 'Insurance certificates',
};

const parseGuidance = (workflow: ParseWorkflowType) =>
  `${PARSE_WORKFLOW_NOUNS[workflow]} can be parsed from ${toTypesLabel(PARSE_EXTENSIONS_BY_WORKFLOW[workflow])}.`;

/**
 * What the upload toast says about parsing (MCV-1761), mirroring the API's promotion of a CV,
 * packet or insurance certificate to its parse workflow: it starts when the file is a format
 * that workflow reads, otherwise the file is stored without parsing. Undefined for a document
 * type that never parses.
 */
export const getUploadParseNotice = (
  doc: { docType: string; docCategory: string },
  fileName: string
): string | undefined => {
  const workflow = resolveParseWorkflow(doc);
  if (!workflow) return undefined;
  if (canParseFile(workflow, fileName)) {
    return 'Parsing has started. Results will appear in your vault when it finishes.';
  }
  const ext = getFileExtension(fileName).toUpperCase();
  return `Stored without parsing: ${ext ? `${ext} files` : 'This file'} cannot be parsed automatically. ${parseGuidance(workflow)}`;
};

/**
 * [MCV-1802] The note on one row of the Add Document list saying what the vault does with that
 * file, so the user learns it before Save rather than from a toast afterwards. [MCV-1810] It is
 * the only place the list says so (the line under the selects is gone), for a lone file too, and
 * it says it for a file that WILL be parsed as well as one that will not. A CV, a packet or a
 * malpractice insurance certificate only accepts a format its workflow parses
 * (documentsUploadExtensions), so a type that resolves to a workflow is always parsed; what is
 * left is a type that is never parsed (a Diploma, a License, Other, a Certificate under any other
 * category). Undefined when no File Type is chosen yet.
 */
export const getRowParseNotice = (
  docType: string | undefined,
  docCategory: string | undefined
): string | undefined => {
  if (!docType) return undefined;
  if (resolveParseWorkflow({ docType, docCategory: docCategory ?? '' })) {
    return 'Will be parsed automatically into your vault.';
  }
  return docType === INSURANCE_CERTIFICATE_UPLOAD.docType
    ? 'Will be uploaded, not parsed. Certificates are parsed only when filed under Malpractice Insurance.'
    : 'Will be uploaded, not parsed.';
};

/**
 * [MCV-1802] Why a stored file cannot be filed under this File Type and Category, or undefined.
 * The edit form's counterpart of the Add Document row error, and the API's PATCH rule
 * (unparseable_filing_detail): whenever the type and category lead to a parse workflow, the file
 * must be a format that workflow reads. A type that is never parsed takes the file as it is.
 */
export const getFilingIssue = (
  fileName: string | null | undefined,
  docType: string | undefined,
  docCategory: string | undefined
): string | undefined => {
  if (!docType) return undefined;
  const workflow = resolveParseWorkflow({ docType, docCategory: docCategory ?? '' });
  if (!workflow) return undefined;
  const supported = `Supported file types: ${toTypesLabel(PARSE_EXTENSIONS_BY_WORKFLOW[workflow])}.`;
  // An older document with no stored file name cannot be judged a parseable format, and the API
  // refuses it under a parsed kind (415): say so here rather than after Save.
  if (!fileName) return `This document has no stored file name. ${supported}`;
  if (canParseFile(workflow, fileName)) return undefined;
  const ext = getFileExtension(fileName).toLowerCase();
  return ext
    ? `.${ext} not supported for this file type`
    : `"${fileName}" has no file extension. ${supported}`;
};

/** A listed file name is cut after this many, then "+N more". */
const MAX_LISTED_UNPARSED = 5;

/**
 * [MCV-1800] The one error alert a batch raises for the files stored without parsing because
 * their format is not one the chosen File Type parses: which files (the first few), which formats
 * cannot be parsed, and the formats each affected workflow does read.
 */
export const getUnparsedAlert = (
  files: { name: string; workflow: ParseWorkflowType }[]
): { text: string; subText: string } => {
  const unique = <T>(items: T[]) => items.filter((item, index) => items.indexOf(item) === index);
  const listed = files.slice(0, MAX_LISTED_UNPARSED).map(({ name }) => name);
  if (files.length > MAX_LISTED_UNPARSED)
    listed.push(`+${files.length - MAX_LISTED_UNPARSED} more`);
  const extensions = unique(
    files.map(({ name }) => getFileExtension(name).toUpperCase()).filter(Boolean)
  );
  const formats =
    extensions.length > 0
      ? `${extensions.join(', ')} files cannot be parsed automatically.`
      : 'These files cannot be parsed automatically.';
  const guidance = unique(files.map(({ workflow }) => workflow)).map(parseGuidance);
  return {
    text: 'Unable to be parsed',
    subText: `Stored without parsing: ${listed.join(', ')}. ${formats} ${guidance.join(' ')}`,
  };
};

interface PopulateEditFormProps {
  event: { type: 'ON_OPEN_MODAL'; payload: { id: number } };
  context: Context;
}
export const populateEditForm = ({ event, context }: PopulateEditFormProps) => {
  const info = context.documentReferences.find(({ id }) => id === event.payload.id);
  if (!info) return { ...context.form };
  return {
    id: info.id,
    docName: info.docName,
    uploadedFileName: info.uploadedFileName,
    docDesc: info.docDesc,
    docType: DOCUMENT_TYPES.find(({ value }) => value === info.docType) || { label: '', value: '' },
    docCategory: DOCUMENT_CATEGORIES.find(({ value }) => value === info.docCategory) || {
      label: '',
      value: '',
    },
  };
};

interface PopulateNewFormProps {
  event: DoneActorEvent<
    {
      id: number;
      docCategory: string;
      docName: string;
      docType: string;
      docDesc: string;
      status: string;
    },
    string
  >;
}
export const populateFormOnUploadSuccess = ({ event }: PopulateNewFormProps) => ({
  docCategory: { label: '', value: event.output.docCategory },
  docName: event.output.docName,
  docType: { label: '', value: event.output.docType },
  docDesc: event.output.docDesc,
  id: event.output.id,
});

interface DownloadProps {
  event: DoneActorEvent<AxiosResponse<Blob, unknown>, string>;
}
export const onDownloadSuccess = ({ event }: DownloadProps) => {
  const response = event.output as AxiosResponse<Blob, unknown>;
  const fileName = response.headers['x-document-name'];
  const fileExt = response.headers?.['x-document-ext'];

  const url = window.URL.createObjectURL(new Blob([event.output.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}.${fileExt}`);
  document.body.appendChild(link);
  link.click();
};

export const isPlaceholderAiJob = (job: AIParseJob) => {
  return String(job.id).startsWith('placeholder-');
};

export const filterRenderableAiJobs = (jobs: AIParseJob[]) => {
  const visibleJobs = jobs.filter((job) => !isPlaceholderAiJob(job));
  return visibleJobs.length > 0 ? visibleJobs : jobs;
};

const ACTIVE_JOB_STATUSES: AIParseJob['job_status'][] = ['PENDING', 'STARTED', 'PROCESSING'];

/** Whether an insurance certificate's latest parse job is still running. */
export const hasActiveInsuranceParse = (documents: DocumentWithAiJobs[]): boolean =>
  documents.some((doc) => {
    if (
      doc.docCategory !== INSURANCE_CERTIFICATE_UPLOAD.docCategory ||
      doc.docType !== INSURANCE_CERTIFICATE_UPLOAD.docType
    ) {
      return false;
    }
    const latestJob = filterRenderableAiJobs(doc.aiJobs).reduce<AIParseJob | null>(
      (latest, job) =>
        !latest || new Date(job.updated_at).getTime() >= new Date(latest.updated_at).getTime()
          ? job
          : latest,
      null
    );
    return latestJob !== null && ACTIVE_JOB_STATUSES.includes(latestJob.job_status);
  });
